import { useState, useEffect, useCallback } from 'react';
import { getSupabaseClient } from '@/lib/supabase';
import {
  COMPANY_INFO_SETTING_KEY,
  CompanyInfo,
  CompanyLocation,
  DEFAULT_COMPANY_INFO,
} from '@/lib/companyInfo';

function parseCompanyInfo(value: unknown): CompanyInfo {
  if (!value || typeof value !== 'object') return DEFAULT_COMPANY_INFO;
  const v = value as Partial<CompanyInfo>;

  const locations = Array.isArray(v.locations)
    ? (v.locations as Partial<CompanyLocation>[])
        .filter(
          (l): l is CompanyLocation =>
            !!l &&
            typeof l === 'object' &&
            typeof l.name === 'string' &&
            typeof l.address === 'string'
        )
        .map((l) => ({
          id: typeof l.id === 'string' ? l.id : `${l.type || 'branch'}-${Math.random().toString(36).slice(2, 7)}`,
          type: l.type === 'hq' ? 'hq' : 'branch',
          name: l.name,
          address: l.address,
          note: typeof l.note === 'string' ? l.note : '',
        }))
    : DEFAULT_COMPANY_INFO.locations;

  return {
    companyName:
      typeof v.companyName === 'string' && v.companyName.trim()
        ? v.companyName
        : DEFAULT_COMPANY_INFO.companyName,
    email: typeof v.email === 'string' && v.email.trim() ? v.email : DEFAULT_COMPANY_INFO.email,
    phoneKr:
      typeof v.phoneKr === 'string' && v.phoneKr.trim()
        ? v.phoneKr
        : DEFAULT_COMPANY_INFO.phoneKr,
    phoneCn:
      typeof v.phoneCn === 'string' && v.phoneCn.trim()
        ? v.phoneCn
        : DEFAULT_COMPANY_INFO.phoneCn,
    locations: locations.length > 0 ? locations : DEFAULT_COMPANY_INFO.locations,
  };
}

export function useCompanyInfo() {
  const [info, setInfo] = useState<CompanyInfo>(DEFAULT_COMPANY_INFO);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', COMPANY_INFO_SETTING_KEY)
        .maybeSingle();

      if (dbError) throw dbError;
      setInfo(parseCompanyInfo(data?.value));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load company info');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async (next: CompanyInfo) => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    setSaving(true);
    try {
      const { error: dbError } = await supabase.from('site_settings').upsert(
        { key: COMPANY_INFO_SETTING_KEY, value: next, updated_at: new Date().toISOString() },
        { onConflict: 'key' }
      );
      if (dbError) throw dbError;

      setInfo(next);
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    info,
    loading,
    error,
    saving,
    save,
    reload: load,
  };
}